import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import ChatIcon from '@mui/icons-material/Chat';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { User } from '../models/User';
import { FavoriteButton } from './FavoriteButton';
import { AppContext } from '../window';
import { useTranslation } from '../i18n';

interface FavoriteUserCardProps {
  user: User;
  onRemove?: (userId: number) => void;
  onChatClick?: (user: User) => void;
  onProfileClick?: (user: User) => void;
  showRemovingState?: boolean;
}

export const FavoriteUserCard: React.FC<FavoriteUserCardProps> = ({
  user,
  onRemove,
  onChatClick,
  onProfileClick,
  showRemovingState = true
}) => {
  const { t } = useTranslation();
  const { texts } = useContext(AppContext);
  const navigate = useNavigate();
  const [isRemoving, setIsRemoving] = useState(false);

  const handleFavoriteChange = (userId: number, isFavorite: boolean) => {
    if (!isFavorite) {
      if (showRemovingState) {
        setIsRemoving(true);
      }
      // Give a small delay for visual feedback before removing
      setTimeout(() => {
        onRemove?.(userId);
      }, 300);
    }
  };

  const handleChatClick = () => {
    if (onChatClick) {
      onChatClick(user);
    } else {
      // Default behavior: navigate to chat
      navigate(`/inbox/${user.id}`);
    }
  };

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick(user);
    } else {
      // Default behavior: navigate to profile
      navigate(`/user/${user.id}`);
    }
  };

  // Truncate description if too long
  const description = user.description?.length > 120 
    ? `${user.description.substring(0, 120)}...` 
    : user.description;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('.badge')) {
      return;
    }
    handleProfileClick();
  };

  return (
    <Card 
      className={`favorite-user-card h-100 ${isRemoving ? 'removing' : ''}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="position-relative">
        <img
          src={user.displayPicture?.thumb || '/default-avatar.png'}
          alt={user.nickname}
          className="w-100 rounded-top"
          style={{ height: 220, objectFit: 'cover', objectPosition: 'center' }}
        />
        <div className="position-absolute top-0 start-0 m-2">
          <FavoriteButton 
            user={user} 
            onFavoriteChange={handleFavoriteChange}
            variant="primary"
            className="shadow-sm"
          />
        </div>
        {user.online ? (
          <Badge bg="success" className="position-absolute top-0 end-0 m-2" style={{ fontSize: 13 }}>
            {t('user.online')}
          </Badge>
        ) : null}
      </div>
      
      <Card.Body className="d-flex flex-column py-2 text-start">
        <div className="fw-bold fs-6 mb-1 letter-spaced">
          {user.nickname}
          {user.age && <span className="text-muted fs-6">, {user.age}</span>}
        </div>
        
        <div className="mb-2 text-muted small">
          <LocationOnIcon fontSize="small" className="me-1" />
          {user.town && user.province ? `${user.town}, ${user.province.name}` : user.country?.name}
        </div>

        {description && (
          <Card.Text className="user-description text-muted small mb-3">
            {description}
          </Card.Text>
        )}

        {user.tags && Object.keys(user.tags).length > 0 && (
          <div className="user-tags mb-3">
            {Object.values(user.tags).slice(0, 3).map((tag) => (
              <Badge key={tag.id} bg="primary" className="me-1 mb-1">
                {tag.name}
              </Badge>
            ))}
            {Object.keys(user.tags).length > 3 && (
              <span className="text-muted small">{t('user.more_tags', { count: Object.keys(user.tags).length - 3 })}</span>
            )}
          </div>
        )}

        <div className="mt-auto">
          <div className="d-flex gap-2 justify-content-center">
            <Button 
              variant="success" 
              className="flex-fill d-flex align-items-center justify-content-center"
              onClick={handleChatClick}
            >
              <ChatIcon fontSize="small" className="me-1" />
              {texts?.chatButton || t('user.chat')}
            </Button>
            <Button 
              variant="outline-primary" 
              className="flex-fill d-flex align-items-center justify-content-center"
              onClick={handleProfileClick}
            >
              <PersonIcon fontSize="small" className="me-1" />
              {texts?.profileButton || t('user.profile')}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};